import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, TrendingUp, TrendingDown, DollarSign, Truck, AlertTriangle, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hebrewMonths = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const MonthlyReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchData = useCallback(async (year, month) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/reports/monthly-summary`;
      if (year && month) url += `?year=${year}&month=${month}`;
      const res = await fetch(url);
      const json = await res.json();
      setData(json);
      if (!selectedYear && json.selectedMonth) {
        setSelectedYear(json.selectedMonth.year);
        setSelectedMonth(json.selectedMonth.month);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { fetchData(); }, []);

  const handleMonthChange = (value) => {
    const [y, m] = value.split('-').map(Number);
    setSelectedYear(y);
    setSelectedMonth(m);
    fetchData(y, m);
  };

  const fmt = (val) => `${val.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border text-right text-sm" dir="rtl">
          <p className="font-bold text-gray-800 mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color }}>
              {p.dataKey === 'income' ? 'הכנסות' : p.dataKey === 'expense' ? 'הוצאות' : 'רווח'}: {fmt(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center"><p className="text-lg text-gray-500">טוען דוח חודשי...</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="text-lg text-red-500">שגיאה בטעינת הדוח</p></div>;

  const monthLabel = selectedMonth ? `${hebrewMonths[selectedMonth - 1]} ${selectedYear}` : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-800" data-testid="monthly-report-title">דוח חודשי</h1>
            {/* Month Selector */}
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border px-3 py-2">
              <Calendar className="w-4 h-4 text-indigo-500" />
              <select
                value={selectedYear && selectedMonth ? `${selectedYear}-${selectedMonth}` : ''}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="bg-transparent text-sm font-medium text-gray-800 outline-none cursor-pointer"
                data-testid="month-selector"
              >
                {selectedYear && selectedMonth && !(data.availableMonths || []).some(m => m.year === selectedYear && m.month === selectedMonth) && (
                  <option value={`${selectedYear}-${selectedMonth}`}>{hebrewMonths[selectedMonth - 1]} {selectedYear}</option>
                )}
                {(data.availableMonths || []).map((m) => (
                  <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                    {hebrewMonths[m.month - 1]} {m.year}
                  </option>
                ))}
              </select>
            </div>
            {[['this-month', 'החודש', 0], ['last-month', 'חודש קודם', -1]].map(([key, label, offset]) => {
              const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset);
              const y = d.getFullYear(); const m = d.getMonth() + 1;
              const active = selectedYear === y && selectedMonth === m;
              return (
                <button key={key} type="button" onClick={() => handleMonthChange(`${y}-${m}`)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-400'}`}
                  data-testid={`month-preset-${key}`}>{label}</button>
              );
            })}
          </div>
          <Button onClick={() => navigate(-1)} variant="outline" className="border-2" data-testid="back-button">
            <ArrowRight className="ml-2 w-5 h-5" />חזרה
          </Button>
        </div>

        {/* Selected Month Label */}
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-lg font-bold">
            <Calendar className="w-5 h-5" />
            {monthLabel}
          </span>
        </div>

        {/* Totals Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-green-600 text-white" data-testid="total-income-card">
            <CardContent className="p-4">
              <p className="text-emerald-100 text-sm">הכנסות {monthLabel}</p>
              <p className="text-2xl font-bold mt-1">₪{fmt(data.totals.income)}</p>
              <TrendingUp className="w-6 h-6 mt-2 text-emerald-200" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-red-500 to-rose-600 text-white" data-testid="total-expense-card">
            <CardContent className="p-4">
              <p className="text-red-100 text-sm">הוצאות {monthLabel}</p>
              <p className="text-2xl font-bold mt-1">₪{fmt(data.totals.expense)}</p>
              <TrendingDown className="w-6 h-6 mt-2 text-red-200" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500 to-indigo-600 text-white" data-testid="total-profit-card">
            <CardContent className="p-4">
              <p className="text-blue-100 text-sm">רווח {monthLabel}</p>
              <p className="text-2xl font-bold mt-1">₪{fmt(data.totals.profit)}</p>
              <DollarSign className="w-6 h-6 mt-2 text-blue-200" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-orange-500 to-amber-600 text-white" data-testid="total-unpaid-card">
            <CardContent className="p-4">
              <p className="text-orange-100 text-sm">חוב לספקים</p>
              <p className="text-2xl font-bold mt-1">₪{fmt(data.totals.totalUnpaidSuppliers)}</p>
              <AlertTriangle className="w-6 h-6 mt-2 text-orange-200" />
            </CardContent>
          </Card>
        </div>

        {/* Monthly Detail Table */}
        {data.monthlyData.length > 0 ? (
          <Card className="shadow-md mb-6">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              <CardTitle className="text-lg">פירוט {monthLabel}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full" data-testid="monthly-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">חודש</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">הכנסות</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">הוצאות</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">רווח</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.monthlyData.map((m) => (
                    <tr key={m.label} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{hebrewMonths[m.month - 1]} {m.year}</td>
                      <td className="px-4 py-3 text-emerald-600 font-medium">₪{fmt(m.income)}</td>
                      <td className="px-4 py-3 text-red-600 font-medium">₪{fmt(m.expense)}</td>
                      <td className={`px-4 py-3 font-bold ${m.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>₪{fmt(m.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-md mb-6">
            <CardContent className="p-8 text-center text-gray-500">
              <p className="text-lg">אין נתונים עבור {monthLabel}</p>
            </CardContent>
          </Card>
        )}

        {/* Suppliers Table */}
        <Card className="shadow-md">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-600 text-white">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="w-5 h-5" />
              קניות לפי ספק - {monthLabel} ({data.suppliers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.suppliers.length > 0 ? (
              <table className="w-full" data-testid="suppliers-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">שם ספק</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">סה"כ קניות</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">שולם</th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">לא שולם</th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">פירוט</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.suppliers.map((s) => (
                    <React.Fragment key={s.supplierName}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{s.supplierName}</td>
                        <td className="px-4 py-3 text-gray-700">₪{fmt(s.totalCost)}</td>
                        <td className="px-4 py-3 text-emerald-600 font-medium">₪{fmt(s.paid)}</td>
                        <td className={`px-4 py-3 font-bold ${s.unpaid > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {s.unpaid > 0 ? `₪${fmt(s.unpaid)}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => setExpandedSupplier(expandedSupplier === s.supplierName ? null : s.supplierName)} className="text-gray-500">
                            {expandedSupplier === s.supplierName ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                      {expandedSupplier === s.supplierName && s.items.map((item, idx) => (
                        <tr key={idx} className="bg-orange-50">
                          <td className="px-6 py-2 text-sm text-gray-600">{item.customer}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">₪{fmt(item.cost)}</td>
                          <td colSpan="2" className="px-4 py-2 text-sm">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${item.paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {item.paid ? 'שולם' : 'לא שולם'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.date}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-4 py-3 text-gray-800">סה"כ</td>
                    <td className="px-4 py-3 text-gray-700">₪{fmt(data.suppliers.reduce((s, x) => s + x.totalCost, 0))}</td>
                    <td className="px-4 py-3 text-emerald-700">₪{fmt(data.suppliers.reduce((s, x) => s + x.paid, 0))}</td>
                    <td className="px-4 py-3 text-red-700">₪{fmt(data.totals.totalUnpaidSuppliers)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p>אין קניות מספקים עבור {monthLabel}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MonthlyReport;
